/**
 * Incident + Security-Graph client — talks to the saf3ai-security-microservice
 * (behind nginx at /security/api → :8095/api). All security data is served from
 * ONE place now; the standalone detection-fabric incident-api is retired.
 *
 * - Incidents: real fabric case shape from MySQL (computed_score, score_factors,
 *   signal_ids, surfaces) + the embedded rule that fired.
 * - Attack graph: projected from the shared Neo4j entity graph with
 *   toxic-combinations + a severity map overlaid.
 * No mock fallback — callers render <ErrorState/> on failure.
 */
import { continueSession } from '../auth/sessionExpiry';
import { withSurface, withSurfaceQuery } from './surfaceScope';
import { reportModuleNotLicensed } from '../../licensing/moduleLockEvents';

const resolveBase = () => {
  if (typeof window !== 'undefined' && window.env && window.env.SECURITY_API_URL) {
    return window.env.SECURITY_API_URL;
  }
  if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SECURITY_API_URL) {
    return process.env.REACT_APP_SECURITY_API_URL;
  }
  // Same-origin behind nginx: /security/api → security-microservice :8095/api
  const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
  return `${origin}/security/api`;
};

const BASE = resolveBase().replace(/\/+$/, '');

const plainQs = (params = {}) => {
  const p = Object.entries(params).filter(([, v]) => v != null && v !== '');
  return p.length ? `?${p.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&')}` : '';
};

// Pinned surface, when these views are mounted inside a product module.
const qs = (rawParams = {}) => plainQs(withSurface(rawParams));

// In-flight GET dedup (AA-025; mirrors GE-016 in enterpriseAIClient.js).
// Concurrent identical reads (Header bell + a page mounting the same query)
// share one promise instead of one request each. Keyed on the full path, so
// the query string is part of the key: different scopes (mine vs all, one
// incident vs the queue) never share. Mutations bypass it entirely. The entry
// lives only for the lifetime of the in-flight promise, so a later load
// still fetches fresh.
const inFlightGets = new Map();

/**
 * Every call a pinned page makes carries the module scope (`?surface=`), reads
 * and writes alike: one incident's detail, graph, evidence, actions, the
 * response ledger, the security-graph exposure, playbook/incident POSTs. The
 * service refuses an incident that is not on the module's surface, and checks
 * the org's licence for that surface; a call without it reads as an aggregate
 * request. The header bell passes `platformWide` and stays unscoped.
 */
const scopedPath = (path) => withSurfaceQuery(path);

function req(rawPath, opts = {}) {
  // `platformWide` skips the module's surface pin: for platform chrome (the header
  // bell) that must show the same thing on every page. Never sent to fetch.
  const { platformWide, ...fetchOpts } = opts || {};
  opts = fetchOpts;
  const path = platformWide ? rawPath : scopedPath(rawPath);
  const method = ((opts && opts.method) || 'GET').toUpperCase();
  const background = Boolean(platformWide);
  if (method !== 'GET') return doReq(path, opts, false, background);
  const key = `GET ${path}`;
  if (inFlightGets.has(key)) return inFlightGets.get(key);
  const promise = doReq(path, opts, false, background)
    .finally(() => { if (inFlightGets.get(key) === promise) inFlightGets.delete(key); });
  inFlightGets.set(key, promise);
  return promise;
}

async function doReq(path, opts = {}, retried = false, background = false) {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwt_token')) || '';
  const res = await fetch(`${BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...opts,
  });
  // Expired token: silently refresh via the platform's refresh_token flow, retry once.
  if (res.status === 401 && !retried) {
    const refreshed = await continueSession().catch(() => false);
    if (refreshed) return doReq(path, opts, true, background);
  }
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    // Parse the envelope rather than truncating it. The service answers a refused action
    // with a structured reason — the activation gate's 409 carries the sentence saying what
    // to do about it — and slicing the raw JSON at 160 characters cut that sentence in half
    // and left an unparseable fragment, so the one useful part never reached the UI.
    let detail = null;
    let human = '';
    try {
      const parsed = JSON.parse(body);
      // 403 module_not_licensed: the page swaps to its lock screen (ModuleLockBoundary).
      // Platform chrome (the header bell) passes platformWide and never locks the page.
      reportModuleNotLicensed(res.status, parsed, { background, url: path });
      detail = (parsed.error && parsed.error.message) ?? parsed.detail ?? parsed;
      human = typeof detail === 'string' ? detail : (detail && detail.message) || '';
    } catch { /* not JSON — fall back to the raw text below */ }
    const err = new Error(human
      || `security-api ${res.status} ${path}${body ? ` — ${body.slice(0, 200)}` : ''}`);
    err.status = res.status;
    err.detail = detail;          // structured, for callers that can act on the reason
    throw err;
  }
  return res.status === 204 ? null : res.json();
}

/* ---- explainable derivations from real fields (no fabricated data) ---- */
const SLA_TARGET_H = { critical: 1, high: 4, medium: 24, low: 72, info: 168 };
const nowS = () => Date.now() / 1000;

const fmtDur = (sec) => {
  const s = Math.max(0, Math.floor(sec));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

export const effectiveSeverity = (i) => i.severity_override || i.effective_severity || i.severity;

export const slaFor = (i) => {
  const target = (SLA_TARGET_H[effectiveSeverity(i)] || 24) * 3600;
  const age = nowS() - (i.first_seen || nowS());
  const remaining = target - age;
  return { remaining, slaBad: remaining < 0, label: remaining <= 0 ? 'breached' : `${fmtDur(remaining)} left` };
};

export const ageLabel = (i) => `${fmtDur(nowS() - (i.first_seen || nowS()))} ago`;

const toEpochSeconds = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1e12 ? Math.floor(value / 1000) : value;
  }
  if (!value) return 0;
  const raw = String(value).trim();
  const asNumber = Number(raw);
  if (Number.isFinite(asNumber) && raw !== '') {
    return asNumber > 1e12 ? Math.floor(asNumber / 1000) : asNumber;
  }
  // The original Custom security endpoint emits UTC timestamps with a space
  // instead of ISO's T/Z. Normalize that shape without changing modern ISO data.
  const isoLike = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)
    ? `${raw.replace(' ', 'T')}Z`
    : raw;
  const milliseconds = Date.parse(isoLike);
  return Number.isFinite(milliseconds) ? Math.floor(milliseconds / 1000) : 0;
};

const normalizeStatus = (status) => {
  if (status === 'active') return 'investigating';
  if (status === 'resolved') return 'closed';
  return status || 'unassigned';
};

/** Accept both incident-stat contracts served during the security migration. */
export const normalizeIncidentStats = (raw = {}) => {
  const statuses = raw.by_status || {};
  const severities = raw.by_severity || {};
  return {
    ...raw,
    by_status: {
      unassigned: statuses.unassigned || 0,
      investigating: statuses.investigating ?? raw.active_incidents ?? 0,
      containing: statuses.containing || 0,
      monitoring: statuses.monitoring || 0,
      contained: statuses.contained || 0,
      closed: statuses.closed ?? raw.resolved_today ?? 0,
    },
    by_severity: {
      critical: severities.critical ?? raw.critical_incidents ?? 0,
      high: severities.high || 0,
      medium: severities.medium || 0,
      low: severities.low || 0,
    },
    // OPEN incidents only (muted excluded). `by_severity` counts every status, so a tile that
    // says "critical open" must read this one. Null when the service predates it: callers then
    // fall back to `by_severity` and must not call that number "open".
    open_by_severity: raw.open_by_severity && typeof raw.open_by_severity === 'object'
      ? {
        critical: raw.open_by_severity.critical || 0,
        high: raw.open_by_severity.high || 0,
        medium: raw.open_by_severity.medium || 0,
        low: raw.open_by_severity.low || 0,
        info: raw.open_by_severity.info || 0,
      }
      : null,
  };
};

/** The engine's one deterministic sentence of what happened, or '' when it has none. */
const summaryOf = (i) => (typeof i.summary === 'string' ? i.summary.trim() : '');

/** Original Custom incident -> a safe Atlas incident detail record. */
export const normalizeIncidentDetail = (incident = {}) => {
  const firstSeen = toEpochSeconds(incident.first_seen || incident.detected_at);
  const updatedAt = toEpochSeconds(
    incident.updated_at || incident.resolved_at || incident.detected_at,
  );
  const affectedAgents = Array.isArray(incident.affected_agents) ? incident.affected_agents : [];
  const threatTypes = Array.isArray(incident.threat_types) ? incident.threat_types : [];
  const timeline = Array.isArray(incident.timeline) ? incident.timeline : [];
  const datePrefix = incident.detected_at && String(incident.detected_at).slice(0, 10);

  return {
    ...incident,
    status: normalizeStatus(incident.status),
    owner: incident.owner || incident.assigned_to || '',
    computed_score: Number.isFinite(Number(incident.computed_score))
      ? Number(incident.computed_score)
      : 0,
    score_factors: incident.score_factors || [],
    signal_ids: incident.signal_ids || [],
    surfaces: incident.surfaces || [],
    entities: {
      agents: affectedAgents,
      identities: [],
      models: [],
      tools: [],
      data_sources: [],
      devices: [],
      ...(incident.entities || {}),
    },
    rule_id: incident.rule_id || threatTypes.join(', ') || '',
    rule: incident.rule || null,
    // What happened (engine sentence) and what the detection means (rule description).
    summary: summaryOf(incident),
    rule_description: typeof incident.rule_description === 'string' ? incident.rule_description.trim() : '',
    // 'signal' = the severity was raised above the rule's level by a matched signal's grade.
    severity_basis: incident.severity_basis || '',
    peak_signal_severity: incident.peak_signal_severity || '',
    first_seen: firstSeen,
    last_seen: toEpochSeconds(incident.last_seen) || updatedAt || firstSeen,
    updated_at: updatedAt || firstSeen,
    findings: incident.findings || [],
    remediation_actions: incident.remediation_actions || [],
    tasks: incident.tasks || [],
    activity: incident.activity || timeline.map((event) => ({
      text: event.event || '',
      actor: event.actor || '',
      ts: toEpochSeconds(
        datePrefix && /^\d{2}:\d{2}:\d{2}$/.test(String(event.timestamp || ''))
          ? `${datePrefix} ${event.timestamp}`
          : event.timestamp,
      ) || firstSeen,
      kind: 'note',
    })),
  };
};

/** fabric Incident -> the row shape the Incidents table renders. */
// first recommended action, normalised to a string (items may be {action,…} objects or, defensively, plain strings)
const firstAction = (inv) => {
  const a = inv && (inv.recommended_actions || [])[0];
  if (!a) return '';
  return typeof a === 'string' ? a : (a.action || '');
};

export const mapIncidentRow = (i) => {
  const firstSeen = toEpochSeconds(i.first_seen || i.detected_at);
  const updatedAt = toEpochSeconds(i.updated_at || i.resolved_at || i.detected_at);
  const computedScore = Number(i.computed_score);
  return {
  id: i.id,
  title: i.title,
  summary: summaryOf(i),
  severityBasis: i.severity_basis || '',
  peakSignalSeverity: i.peak_signal_severity || '',
  // who raised it: hunt / triage = an analyst; otherwise a fabric tier (see incidents/tier.js)
  source: i.source || '',
  // shadow = a rule still being measured; the queue hides these unless asked (include_shadow)
  ruleState: i.rule_state || 'production',
  // Actors, split into the two the queue shows as their own column: which agents the
  // incident touched and which users drove it. Straight from the incident's resolved
  // entities, so it works for every incident regardless of when it was raised.
  agents: (i.entities && i.entities.agents) || i.affected_agents || [],
  identities: (i.entities && i.entities.identities) || [],
  surfaces: i.surfaces || [],
  score: Number.isFinite(computedScore) ? Math.round(computedScore) : 0,
  severity: effectiveSeverity(i),
  signals: (i.signal_ids || []).length,
  rule: i.rule_id || (i.threat_types || []).join(', '),
  status: normalizeStatus(i.status),
  owner: i.owner || i.assigned_to || '',
  updatedAt: updatedAt || 0,
  lastActor: i.last_actor || '',
  touchedBy: i.touched_by || [],  // everyone who has acted — powers the 'touched by me' filter
  override: !!i.severity_override,
  age: ageLabel({ ...i, first_seen: firstSeen }),
  ts: toEpochSeconds(i.last_seen) || firstSeen || 0,
  // auto-triage resolution — a benign case the AI closed on its own. Kept visible (never hidden),
  // just labelled 'auto-closed' so the analyst can see (and reopen) what the platform decided.
  closeReason: i.close_reason || '',
  autoClosed: i.status === 'closed' && i.close_reason === 'auto-triaged-benign',
  // merge state — a 'merged' incident is a tombstone folded into `mergedInto`; a survivor
  // carries `mergedFrom` (the incidents folded into it). Both kept visible + reversible.
  merged: i.status === 'merged',
  mergedInto: i.merged_into || '',
  mergedFrom: i.merged_from || [],
  // AI verdict (present once investigated) — powers the decision-card view; absent → card
  // shows "not yet assessed" rather than fabricating one.
  investigated: !!i.investigation,
  assessment: (i.investigation && i.investigation.assessment) || '',
  oneLiner: (i.investigation && i.investigation.one_liner) || '',
  confidence: i.investigation && typeof i.investigation.confidence === 'number' ? i.investigation.confidence : null,
  // first recommended action as a STRING — recommended_actions items are {action, rationale, urgency}
  // objects, so pull .action (never the object; rendering an object crashes React).
  recommended: firstAction(i.investigation),
  };
};

const incidentRows = (data) => (Array.isArray(data) ? data : (data && data.incidents) || []);

/**
 * POST that answers with server-sent events: { type: 'event', event } for each step the agent takes,
 * then { type: 'result', ... } or { type: 'error', detail }. Used by the investigation and by a
 * challenge re-run. Resolves when the stream ends; a refused request reports the server's own reason.
 */
const streamRun = async (path, body, handlers = {}) => {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwt_token')) || '';
  let res;
  try {
    res = await fetch(`${BASE}${withSurfaceQuery(path)}`, {
      method: 'POST',
      signal: handlers.signal,
      headers: {
        Accept: 'text/event-stream',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (e) { if (handlers.onError) handlers.onError((e && e.message) || 'stream failed'); return; }
  if (!res.ok || !res.body) {
    let why = `stream ${res.status}`;
    try {
      const j = await res.json();
      why = (j && ((j.error && j.error.message) || (typeof j.detail === 'string' && j.detail))) || why;
    } catch (e) { /* the status alone */ }
    if (handlers.onError) handlers.onError(why);
    return;
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf('\n\n')) >= 0) {
      const line = buf.slice(0, idx).split('\n').find((l) => l.startsWith('data:'));
      buf = buf.slice(idx + 2);
      if (!line) continue;
      let ev;
      try { ev = JSON.parse(line.slice(5).trim()); } catch (e) { continue; }
      if (ev.type === 'event' && handlers.onEvent) handlers.onEvent(ev.event);
      else if (ev.type === 'result' && handlers.onResult) handlers.onResult(ev);
      else if (ev.type === 'error' && handlers.onError) handlers.onError(ev.detail || 'Investigation failed');
    }
  }
};

export const incidentApi = {
  base: BASE,
  listIncidents: (params = {}) => req(`/v1/incidents/${qs(params)}`).then(incidentRows),
  getStats: (params = {}) => req(`/v1/incidents/stats${qs(params)}`).then(normalizeIncidentStats),
  // Time-to-X + the autonomy scorecard. Derived from lifecycle timestamps the platform has
  // always written, so it answers "are we getting faster?" retroactively.
  getMetrics: (params = {}) => req(`/v1/incidents/metrics${qs(params)}`),
  getIncident: async (id) => {
    try {
      return normalizeIncidentDetail(await req(`/v1/incidents/${encodeURIComponent(id)}`));
    } catch (error) {
      // The original Custom endpoint lists complete records but its per-id
      // handler returns 404. Resolve a selected record from that list.
      if (error.status !== 404) throw error;
      const rows = incidentRows(await req(`/v1/incidents/${qs({ size: 500 })}`));
      const match = rows.find((incident) => String(incident.id) === String(id));
      if (!match) throw error;
      return normalizeIncidentDetail(match);
    }
  },
  // Attack path from the shared Neo4j graph (toxic-combinations + severity).
  getIncidentGraph: (id) => req(`/v1/incidents/${encodeURIComponent(id)}/graph`),
  // Raw records behind the incident — the actual prompts/responses that were flagged.
  getIncidentEvidence: (id, params = {}) => req(`/v1/incidents/${encodeURIComponent(id)}/evidence${qs(params)}`),
  // Whole-org security map (toxic-combinations + severity) for a dedicated view.
  // Through qs() so a surface-pinned mount narrows the map to its own estate. The
  // whole-org fetch is capped, and org 3's cap fills entirely with gcp_audit resource
  // nodes, so an unscoped call renders a map with none of the surface's agents on it.
  securityMap: (params = {}) => req(`/v1/security-graph/network${qs(params)}`),
  toxicCombinations: (params = {}) => req(`/v1/security-graph/toxic-combinations${qs(params)}`),
  // Attack-path prioritisation — the single fixes that break the most toxic combinations.
  exposure: () => req('/v1/security-graph/exposure'),
  // Chain of custody for one case: what we sent to a system outside Saf3AI, what it
  // answered, who approved it, and the handle that would undo it. The remediation list on
  // the incident is what the case view renders; this is the durable record underneath it.
  actionLedger: (incidentId) => req(`/v1/response/actions?incident_id=${encodeURIComponent(incidentId)}`),


  // Related incidents — the whole blast radius: other open cases that share this
  // one's entities / rule / surface, so an analyst never works a repeat in isolation.
  getRelated: (id, params = {}) => req(`/v1/incidents/${encodeURIComponent(id)}/related${qs(params)}`),
  // precedent — how the SOC resolved cases like this before (memory, not re-deciding from scratch)
  getPrecedent: (id) => req(`/v1/incidents/${encodeURIComponent(id)}/precedent`),

  // case tasks/checklist (the "leave breadcrumbs" half of the loop) + shift handoff
  addTask: (id, text, assignee) => req(`/v1/incidents/${encodeURIComponent(id)}/tasks`, { method: 'POST', body: JSON.stringify({ text, assignee }) }),
  updateTask: (id, taskId, patch) => req(`/v1/incidents/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteTask: (id, taskId) => req(`/v1/incidents/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}`, { method: 'DELETE' }),
  handoff: (params = {}) => req(`/v1/incidents/handoff/queue${qs(params)}`),

  // playbook execution (Phase 2) — run native steps for real, log to the timeline
  runPlaybook: (id, playbookId, testParams) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/run-playbook`, { method: 'POST', body: JSON.stringify({ playbook_id: playbookId, ...(testParams ? { test_params: testParams } : {}) }) }),
  // What a Test step (red-team re-test / code scan) can be pointed at: the targets and repos the
  // caller may pick, the ones matching the incident's agent marked recommended.
  testOptions: (id) => req(`/v1/incidents/${encodeURIComponent(id)}/test-options`),
  runStep: (id, playbookId, stepN, params) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/run-step`, { method: 'POST', body: JSON.stringify({ playbook_id: playbookId, step_n: stepN, ...(params ? { params } : {}) }) }),
  // Check on this incident's running Test skills (red-team re-tests). A finished one pins its
  // finding and, if it passed, resumes the workflow. The detail page polls this while one runs.
  refreshTests: (id) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/tests/refresh`, { method: 'POST', body: JSON.stringify({}) }),
  // playbook attach — bind a recommended/chosen playbook to a case (optionally run now).
  // Candidates come from playbooksApi.recommend; these two PERSIST the choice on the case.
  attachPlaybook: (id, playbookId, run = false) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/attach-playbook`, { method: 'POST', body: JSON.stringify({ playbook_id: playbookId, run }) }),
  detachPlaybook: (id) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/detach-playbook`, { method: 'POST' }),
  // Response-Agent feedback consumer — the analyst steering/outcome corpus + the
  // rules analysts keep overriding (tuning candidates). Powers the tuning surface.
  listFeedback: (params = {}) => req(`/v1/incidents/feedback${qs(params)}`),
  // Investigation Agent — read-only AI analyst: gathers evidence + attack path + timeline,
  // reasons over them, returns a grounded verdict (assessment / root cause / scope /
  // severity call / recommended actions) and persists it on the case. Never writes/enforces.
  investigate: (id) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/investigate`, { method: 'POST' }),
  // The same investigation, streamed: each step the agent takes arrives as it happens
  // (handlers.onEvent), then the finished result (handlers.onResult). `deep` works the case: it looks
  // things up, weighs competing explanations and changes course. Resolves when the stream ends.
  investigateStream: (id, handlers = {}) => streamRun(
    `/v1/incidents/${encodeURIComponent(id)}/investigate?stream=true${handlers.deep ? '&deep=true' : ''}`,
    undefined,
    handlers,
  ),
  // Stored runs (the trace of what the agent did), replay with a challenge, and analyst feedback.
  listInvestigationRuns: (id) => req(`/v1/incidents/${encodeURIComponent(id)}/investigation/runs`),
  getInvestigationRun: (id, runId) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/investigation/runs/${encodeURIComponent(runId)}`),
  replayInvestigation: (id, body) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/investigation/replay`, { method: 'POST', body: JSON.stringify(body) }),
  // The same challenge re-run, streamed: each step as it happens (handlers.onEvent), then the result
  // (handlers.onResult: { run_id, investigation, diff, events, adopted }).
  replayInvestigationStream: (id, body, handlers = {}) => streamRun(
    `/v1/incidents/${encodeURIComponent(id)}/investigation/replay?stream=true`,
    body,
    handlers,
  ),
  investigationFeedback: (id, body) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/investigation/feedback`, { method: 'POST', body: JSON.stringify(body) }),
  listInvestigationFeedback: (id) => req(`/v1/incidents/${encodeURIComponent(id)}/investigation/feedback`),
  // Auto-triage — the platform DECIDES: investigate open un-investigated incidents and
  // auto-close the confidently-benign ones (nothing critical, high bar, reversible, audited).
  autoTriage: (limit = 12, minConfidence = 0.8) =>
    req('/v1/incidents/auto-triage', { method: 'POST', body: JSON.stringify({ limit, min_confidence: minConfidence }) }),
  // Autonomy (Phase 2) — the platform ACTS: auto-execute the reversible response steps for a
  // confirmed, high-confidence incident, parking the irreversible ones for approval.
  autoRespond: (id, playbookId = null) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/auto-respond`, { method: 'POST', body: JSON.stringify(playbookId ? { playbook_id: playbookId } : {}) }),
  getAutonomyPolicy: () => req('/v1/incidents/autonomy/policy'),
  setAutonomyPolicy: (patch) => req('/v1/incidents/autonomy/policy', { method: 'PUT', body: JSON.stringify(patch) }),
  // The execution trace behind an incident — agent runs, hand-offs, tool and model calls,
  // rebuilt from the OTEL spans already indexed. Answers "what did the agent DO", where the
  // evidence tab answers "what was said".
  getTrajectory: (id) => req(`/v1/incidents/${encodeURIComponent(id)}/trajectory`),
  // one span of that tree, in full — prompt, response, tool call and the whole raw record
  getTrajectorySpan: (id, index, docId) => req(`/v1/incidents/${encodeURIComponent(id)}/trajectory/span${qs({ index, id: docId })}`),
  // Feedback flywheel — learn from approve/reject decisions; recommends dial changes.
  getAutonomyInsights: () => req('/v1/incidents/autonomy/insights'),
  // Emergency stop. Separate from the `enabled` dial: halting parks everything immediately and
  // resuming restores the org's configured policy untouched, so nobody has to reconstruct it
  // mid-incident.
  haltAutonomy: (reason = '') =>
    req('/v1/incidents/autonomy/halt', { method: 'POST', body: JSON.stringify({ reason }) }),
  resumeAutonomy: () => req('/v1/incidents/autonomy/resume', { method: 'POST', body: '{}' }),
  // Every action the platform took on its own, across all incidents — the cross-incident audit
  // view. Also carries the hourly action budget and the current halt state.
  getAutonomyLedger: (params = {}) => req(`/v1/incidents/autonomy/ledger${qs(params)}`),
  // Automation rules — "run this playbook the next time this kind of incident happens".
  // Simulate records what approve mode would do; approve runs it, parking estate changes.
  listAutomationRules: () => req('/v1/incidents/autonomy/rules'),
  createAutomationRule: (rule) =>
    req('/v1/incidents/autonomy/rules', { method: 'POST', body: JSON.stringify(rule) }),
  updateAutomationRule: (id, patch) =>
    req(`/v1/incidents/autonomy/rules/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteAutomationRule: (id) =>
    req(`/v1/incidents/autonomy/rules/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  // Response Agent (Phase 3) — LLM plans a tailored response + executes safe steps
  agentRespond: (id, autoExecute = true) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/agent-respond`, { method: 'POST', body: JSON.stringify({ auto_execute: autoExecute }) }),
  execStep: (id, step) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/exec-step`, { method: 'POST', body: JSON.stringify({ step }) }),
  // collaborative agent chat (human-in-the-loop) + ad-hoc plan run + feedback capture
  agentChat: (id, messages) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/agent-chat`, { method: 'POST', body: JSON.stringify({ messages }) }),
  // Streams the agent's reasoning live (SSE). handlers: { onThinking(delta), onPlan(ev), onError(msg), onDone() }.
  agentChatStream: async (id, messages, handlers = {}) => {
    const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwt_token')) || '';
    let res;
    try {
      res = await fetch(`${BASE}${withSurfaceQuery(`/v1/incidents/${encodeURIComponent(id)}/agent-chat-stream`)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ messages }),
      });
    } catch (e) { if (handlers.onError) handlers.onError(e.message); return; }
    if (!res.ok || !res.body) { if (handlers.onError) handlers.onError(`stream ${res.status}`); return; }
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = '';
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf('\n\n')) >= 0) {
        const line = buf.slice(0, idx).split('\n').find((l) => l.startsWith('data:'));
        buf = buf.slice(idx + 2);
        if (!line) continue;
        let ev;
        try { ev = JSON.parse(line.slice(5).trim()); } catch (e) { continue; }
        if (ev.type === 'thinking' && handlers.onThinking) handlers.onThinking(ev.text);
        else if (ev.type === 'plan' && handlers.onPlan) handlers.onPlan(ev);
        else if (ev.type === 'error' && handlers.onError) handlers.onError(ev.text);
        else if (ev.type === 'done' && handlers.onDone) handlers.onDone();
      }
    }
  },
  runPlan: (id, steps) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/run-plan`, { method: 'POST', body: JSON.stringify({ steps }) }),
  agentFeedback: (id, body) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/agent-feedback`, { method: 'POST', body: JSON.stringify(body) }),

  // governed writes (real mutations on the incident doc, audited to its activity log)
  assign: (id, owner) => req(`/v1/incidents/${encodeURIComponent(id)}/assign`, { method: 'POST', body: JSON.stringify({ owner }) }),
  setStatus: (id, status, note = '') =>
    req(`/v1/incidents/${encodeURIComponent(id)}/status`, { method: 'POST', body: JSON.stringify({ status, note }) }),
  addNote: (id, text) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/note`, { method: 'POST', body: JSON.stringify({ text }) }),
  overrideSeverity: (id, severity, justification) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/severity`, { method: 'POST', body: JSON.stringify({ severity, justification }) }),
  // Clear the override: severity null returns the incident to its computed level. Audited like
  // an override, so the justification is still required.
  revertSeverity: (id, justification) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/severity`, { method: 'POST', body: JSON.stringify({ severity: null, justification }) }),
  close: (id, resolution = 'resolved') =>
    req(`/v1/incidents/${encodeURIComponent(id)}/close`, { method: 'POST', body: JSON.stringify({ resolution }) }),

  // Case-level approver — every approval parked for this incident routes here (unless a step names
  // its own approver). Empty clears it → falls back to the incident owner.
  setApprover: (id, approver) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/approver`, { method: 'POST', body: JSON.stringify({ approver }) }),

  // Org-wide default approver PER step-action verb — reused across ALL workflows. A step that names
  // its own approver still overrides; else this wins over the incident owner.
  stepApprovers: () => req('/v1/incidents/config/step-approvers'),
  setStepApprover: (action, approver) =>
    req('/v1/incidents/config/step-approvers', { method: 'POST', body: JSON.stringify({ action, approver }) }),

  // Remediation actions — the durable record of what the platform actually DID (real guardrail/
  // mute) vs. requested-and-pending-an-enforcement-plane. Reversible ones can be reverted.
  getRemediations: (id) => req(`/v1/incidents/${encodeURIComponent(id)}/remediations`),
  revertRemediation: (id, raId) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/remediations/${encodeURIComponent(raId)}/revert`, { method: 'POST', body: JSON.stringify({}) }),

  // Merge duplicate incidents INTO this one (the survivor); reversible via unmerge.
  merge: (survivorId, mergeIds, reason = '') =>
    req(`/v1/incidents/${encodeURIComponent(survivorId)}/merge`, { method: 'POST', body: JSON.stringify({ merge_ids: mergeIds, reason }) }),
  unmerge: (id) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/unmerge`, { method: 'POST', body: JSON.stringify({}) }),

  // Agent tool surface — the action Skills recommended for this incident (from its Workflow), and
  // invoking one through the enforcement dispatcher (reversible/approval/audited).
  recommendedSkills: (id) => req(`/v1/incidents/${encodeURIComponent(id)}/skills`),
  invokeSkill: (id, skillId, confirm = false, params = {}) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/skills/invoke`, { method: 'POST', body: JSON.stringify({ skill_id: skillId, confirm, params }) }),
  saveSkillsAsWorkflow: (id, name, skillIds) =>
    req(`/v1/incidents/${encodeURIComponent(id)}/skills/save-workflow`, { method: 'POST', body: JSON.stringify({ name, skill_ids: skillIds }) }),

  // the caller's resolved identity — match 'assigned to me' against the incident owner
  whoami: () => req('/v1/incidents/whoami'),
  // active users in the caller's org — for the assignee picker (choose a person, not type an email)
  orgMembers: () => req('/v1/incidents/org-members'),
  // everyone an approval can be routed to (people + the org's roles) and where approvals go today
  approverOptions: () => req('/v1/incidents/approver-options'),
  // durable approvals queue (df_approvals) — parked approval-gated steps that real runs
  // left for sign-off. approve executes the step for real + resolves the record.
  listApprovals: (params = {}) => req(`/v1/incidents/approvals${qs(params)}`),
  // The same queue across every surface, whichever module page is open: the header
  // bell's "Needs you" is platform-wide, unlike the module's own Response page.
  listApprovalsPlatformWide: (params = {}) =>
    req(`/v1/incidents/approvals${plainQs(params)}`, { platformWide: true }),
  approveApproval: (approvalId, note = '', params) =>
    req(`/v1/incidents/approvals/${encodeURIComponent(approvalId)}/approve`, { method: 'POST', body: JSON.stringify({ note, ...(params ? { params } : {}) }) }),
  // Ask again on a step that was rejected. A NEW record is parked; the rejection stays in
  // the history with its note, because what happened is not edited by what happens next.
  reopenApproval: (approvalId) =>
    req(`/v1/incidents/approvals/${encodeURIComponent(approvalId)}/reopen`, { method: 'POST', body: JSON.stringify({}) }),
  rejectApproval: (approvalId, note = '') =>
    req(`/v1/incidents/approvals/${encodeURIComponent(approvalId)}/reject`, { method: 'POST', body: JSON.stringify({ note }) }),
  // Escalate every approval past its SLA. A breached approval means an incident is sitting
  // uncontained with nobody accountable; this routes them to the escalation approver (or back to
  // the shared queue) and stamps the incident timeline. Idempotent — re-running never re-pages.
  escalateOverdueApprovals: () =>
    req('/v1/incidents/approvals/escalate-overdue', { method: 'POST', body: '{}' }),
  // route a pending approval to a named approver → their "assigned to me" queue
  reassignApproval: (approvalId, approver) =>
    req(`/v1/incidents/approvals/${encodeURIComponent(approvalId)}/reassign`, { method: 'POST', body: JSON.stringify({ approver }) }),
};

export default incidentApi;
